import React, { useState } from "react";
import axios from "axios";
import { format } from "date-fns";
import { toast } from "sonner";
import { CalendarIcon, Upload, X, Loader2, Flower2 } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { Calendar } from "../ui/calendar";
import { Reveal, Eyebrow } from "./Reveal";
import { SERVICES } from "../../data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const field =
  "w-full rounded-xl bg-[#0d0d0d] border border-white/12 px-4 py-3.5 text-white placeholder-white/35 outline-none focus:border-[#F56FB4] transition-colors duration-200";

export const QuoteForm = () => {
  const [form, setForm] = useState({ name: "", phone: "", email: "", address: "", service: "", description: "" });
  const [date, setDate] = useState(null);
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  const onFiles = (e) => {
    const incoming = Array.from(e.target.files || []);
    setFiles((prev) => [...prev, ...incoming].slice(0, 6));
  };
  const removeFile = (idx) => setFiles((prev) => prev.filter((_, i) => i !== idx));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.email) {
      toast.error("Please fill in your name, phone, and email.");
      return;
    }
    setLoading(true);
    try {
      const data = new FormData();
      Object.entries(form).forEach(([k, v]) => data.append(k, v));
      data.append("preferred_date", date ? format(date, "yyyy-MM-dd") : "");
      files.forEach((f) => data.append("photos", f));
      const res = await axios.post(`${API}/quote`, data, { headers: { "Content-Type": "multipart/form-data" } });
      if (res.data.status === "success") {
        toast.success("Request sent! We'll be in touch shortly. 🌸");
        setForm({ name: "", phone: "", email: "", address: "", service: "", description: "" });
        setDate(null);
        setFiles([]);
      }
    } catch (err) {
      toast.error("Something went wrong. Please try again or call us.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="quote" className="relative py-24 md:py-32 bg-[#111111] overflow-hidden">
      <div className="absolute -left-24 top-1/4 opacity-[0.05] pointer-events-none">
        <Flower2 size={420} className="text-[#F56FB4]" />
      </div>
      <div className="max-w-3xl mx-auto px-6 md:px-10 relative z-10">
        <div className="text-center mb-10">
          <Reveal><Eyebrow color="#F56FB4">Free Estimate</Eyebrow></Reveal>
          <Reveal delay={0.05}>
            <h2 className="font-heading font-extrabold uppercase text-white text-4xl md:text-5xl lg:text-6xl tracking-tight mt-3">
              Get My Free <span className="text-[#53C653]">Estimate</span>
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="text-white/60 mt-4">Fast, easy, and no obligation. Tell us about your space.</p>
          </Reveal>
        </div>

        <Reveal delay={0.1}>
          <form
            onSubmit={submit}
            data-testid="quote-form"
            className="rounded-[2rem] border border-white/10 bg-[#161616]/70 backdrop-blur-xl p-6 md:p-10 shadow-[0_30px_80px_rgba(0,0,0,0.5)]"
          >
            <div className="grid md:grid-cols-2 gap-4">
              <input data-testid="quote-name" className={field} placeholder="Full Name *" value={form.name} onChange={set("name")} />
              <input data-testid="quote-phone" className={field} placeholder="Phone Number *" value={form.phone} onChange={set("phone")} />
              <input data-testid="quote-email" type="email" className={field} placeholder="Email Address *" value={form.email} onChange={set("email")} />
              <input data-testid="quote-address" className={field} placeholder="Property Address" value={form.address} onChange={set("address")} />

              <Select value={form.service} onValueChange={(v) => setForm((p) => ({ ...p, service: v }))}>
                <SelectTrigger data-testid="quote-service" className={`${field} data-[placeholder]:text-white/35 h-auto`}>
                  <SelectValue placeholder="Service Needed" />
                </SelectTrigger>
                <SelectContent className="bg-[#161616] border-white/10 text-white">
                  {SERVICES.map((s) => (
                    <SelectItem key={s.title} value={s.title} className="focus:bg-[#F56FB4]/20 focus:text-white">{s.title}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Popover>
                <PopoverTrigger asChild>
                  <button type="button" data-testid="quote-date" className={`${field} flex items-center justify-between text-left ${date ? "text-white" : "text-white/35"}`}>
                    {date ? format(date, "PPP") : "Preferred Date"}
                    <CalendarIcon size={18} className="text-[#53C653]" />
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0 bg-[#161616] border-white/10" align="start">
                  <Calendar mode="single" selected={date} onSelect={setDate} disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))} initialFocus className="text-white" />
                </PopoverContent>
              </Popover>
            </div>

            <textarea data-testid="quote-description" className={`${field} mt-4 min-h-[110px] resize-y`} placeholder="Describe what you'd like removed..." value={form.description} onChange={set("description")} />

            {/* Photo upload */}
            <div className="mt-4">
              <label
                htmlFor="photo-upload"
                data-testid="quote-photo-label"
                className="flex flex-col items-center justify-center gap-2 rounded-xl border border-dashed border-white/20 bg-[#0d0d0d] px-4 py-8 cursor-pointer hover:border-[#F56FB4] transition-colors duration-200"
              >
                <Upload size={24} className="text-[#F56FB4]" />
                <span className="text-sm text-white/70">Upload before photos (up to 6)</span>
                <span className="text-xs text-white/35">PNG, JPG — helps us give an accurate quote</span>
              </label>
              <input id="photo-upload" data-testid="quote-photo-input" type="file" accept="image/*" multiple className="hidden" onChange={onFiles} />
              {files.length > 0 && (
                <div className="flex flex-wrap gap-3 mt-3" data-testid="quote-photo-previews">
                  {files.map((f, i) => (
                    <div key={i} className="relative h-16 w-16 rounded-lg overflow-hidden border border-white/10">
                      <img src={URL.createObjectURL(f)} alt="preview" className="h-full w-full object-cover" />
                      <button type="button" onClick={() => removeFile(i)} className="absolute top-0.5 right-0.5 h-5 w-5 rounded-full bg-black/70 flex items-center justify-center text-white">
                        <X size={12} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <button
              type="submit"
              data-testid="quote-submit"
              disabled={loading}
              className="mt-6 w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#F56FB4] px-8 py-4 font-semibold text-white transition-transform duration-200 hover:scale-[1.02] shadow-[0_0_25px_rgba(245,111,180,0.4)] disabled:opacity-60"
            >
              {loading ? <><Loader2 size={18} className="animate-spin" /> Sending...</> : <><Flower2 size={18} /> Get My Free Estimate</>}
            </button>
          </form>
        </Reveal>
      </div>
    </section>
  );
};

export default QuoteForm;
